/**
 * PostHog, gated on cookie consent — the whole point of asking first.
 *
 * PostHog is optional at the infrastructure level too: `VITE_POSTHOG_KEY`
 * is blank until a PostHog project exists, and every function here is a
 * no-op without it. That mirrors how Google sign-in degrades (§8, Phase 3):
 * the feature is fully wired, just inert until configured. PLAN.md Phase 8.
 */
import posthog from 'posthog-js';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST ?? 'https://us.i.posthog.com';

export const CONSENT_STORAGE_KEY = 'cookie-consent';

export type ConsentChoice = 'accepted' | 'rejected';

export function getStoredConsent(): ConsentChoice | null {
  const value = localStorage.getItem(CONSENT_STORAGE_KEY);
  return value === 'accepted' || value === 'rejected' ? value : null;
}

let initialized = false;

/** Starts PostHog. Safe to call more than once — only the first call does anything. */
function startAnalytics(): void {
  if (initialized || !POSTHOG_KEY) return;
  initialized = true;
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // We ask before PostHog ever loads (see CookieConsent.tsx), so its own
    // internal consent gate would just be asking a second time.
    opt_out_capturing_by_default: false,
    capture_pageview: true,
    autocapture: true,
  });
}

/** Stops sending events and clears local PostHog state, for a revoked or rejected choice. */
function stopAnalytics(): void {
  if (initialized) posthog.opt_out_capturing();
}

/** Applies a consent choice: starts or stops PostHog, and remembers the choice. */
export function setConsent(choice: ConsentChoice): void {
  localStorage.setItem(CONSENT_STORAGE_KEY, choice);
  if (choice === 'accepted') startAnalytics();
  else stopAnalytics();
}

/** Re-applies whatever choice (if any) was already stored — call once at startup. */
export function applyStoredConsent(): void {
  if (getStoredConsent() === 'accepted') startAnalytics();
}
